<template>
  <woot-modal :show="show" :on-close="closeModal">
    <div class="column content-box">
      <woot-modal-header :header-title="header" />

      <form class="row" @submit.prevent="submit">
        <woot-input
          v-model.trim="app.title"
          :class="{ error: $v.app.title.$error }"
          class="medium-12 columns"
          :label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_LABEL')"
          :placeholder="
            $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_PLACEHOLDER')
          "
          :error="
            $v.app.title.$error
              ? $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_ERROR')
              : null
          "
          data-testid="app-title"
          @input="$v.app.title.$touch"
        />
        <div class="medium-12 columns">
          <label class="radio-group-label">Tipo</label>
          <div class="radio-group">
            <label class="radio-group-item-label">
              <input
                v-model="app.content.type"
                class="radio-input"
                type="radio"
                value="frame"
              />
              <span>Chat</span>
            </label>
            <label class="radio-group-item-label">
              <input
                v-model="app.content.type"
                class="radio-input"
                type="radio"
                value="frameFull"
              />
              <span>Página</span>
            </label>
          </div>
        </div>
        <woot-input
          v-model.trim="app.content.url"
          :class="{ error: $v.app.content.url.$error }"
          class="medium-12 columns app--url_input"
          :label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_LABEL')"
          :placeholder="
            $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_PLACEHOLDER')
          "
          :error="
            $v.app.content.url.$error
              ? $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_ERROR')
              : null
          "
          data-testid="app-url"
          @input="$v.app.content.url.$touch"
        />
        <div class="modal-footer">
          <div class="medium-12 columns">
            <woot-button
              :is-loading="isLoading"
              :is-disabled="$v.$invalid"
              data-testid="label-submit"
              class="bg-primary"
            >
              {{ submitButtonLabel }}
            </woot-button>
            <woot-button class="button clear" @click.prevent="closeModal">
              {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.CREATE.FORM_CANCEL') }}
            </woot-button>
          </div>
        </div>
      </form>
    </div>
  </woot-modal>
</template>

<script>
import { required, url } from 'vuelidate/lib/validators';
import alertMixin from 'shared/mixins/alertMixin';

export default {
  mixins: [alertMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
    modalOrigin: {
      type: String,
      default: 'frame',
    },
    selectedAppData: {
      type: Object,
      default: () => ({}),
    },
  },
  validations: {
    app: {
      title: { required },
      content: {
        type: { required },
        url: { required, url },
      },
    },
  },
  data() {
    return {
      isLoading: false,
      app: {
        title: '',
        content: {
          type: this.modalOrigin,
          url: '',
        },
      },
    };
  },
  computed: {
    header() {
      return this.$t(`INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.HEADER`);
    },
    submitButtonLabel() {
      return this.$t(
        `INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.FORM_SUBMIT`
      );
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.initializeForm();
      }
    },
    mode() {
      if (this.show) {
        this.initializeForm();
      }
    },
    selectedAppData: {
      handler() {
        if (this.show && String(this.mode).toUpperCase() === 'UPDATE') {
          this.initializeForm();
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.initializeForm();
  },
  methods: {
    initializeForm() {
      const isUpdate = String(this.mode).toUpperCase() === 'UPDATE';
      if (isUpdate && this.selectedAppData) {
        this.app = {
          title: this.selectedAppData.title || '',
          content: (this.selectedAppData.content &&
            this.selectedAppData.content[0]) || {
            type: this.modalOrigin,
            url: '',
          },
        };
      } else {
        this.app = {
          title: '',
          content: { type: this.modalOrigin, url: '' },
        };
      }
    },
    closeModal() {
      // Reset the data once closed
      this.app = {
        title: '',
        content: { type: this.modalOrigin, url: '' },
      };
      this.$emit('close');
    },
    async submit() {
      try {
        this.$v.$touch();
        if (this.$v.$invalid) {
          return;
        }

        const action = this.mode.toLowerCase();
        const payload = {
          title: this.app.title,
          content: [this.app.content],
        };

        if (action === 'update') {
          payload.id = this.selectedAppData.id;
        }

        this.isLoading = true;
        await this.$store.dispatch(`dashboardApps/${action}`, payload);
        this.showAlert(
          this.$t(
            `INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.API_SUCCESS`
          )
        );
        this.closeModal();
      } catch (err) {
        this.showAlert(
          this.$t(`INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.API_ERROR`)
        );
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.content-row {
  display: flex;
  align-items: center;
  width: 100%;
  .app--url_input {
    flex: 1;
  }
  .app--url_add_btn {
    margin-left: var(--space-one);
    margin-top: var(--space-one);
  }
}

.radio-group-label {
  margin-bottom: var(--space-smaller);
}
.radio-group {
  display: inline-flex;
  gap: var(--space-one);
  margin-bottom: var(--space-small);
}
.radio-group-item-label {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}
.radio-input {
  margin: 0 var(--space-smaller) 0 0;
}
</style>
